import { useCallback, type ReactNode } from 'react';
import { Card, Flex } from 'antd';
import { IconClipboardList } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { OemPartnerGroupDto } from '@evimo/client/models';
import {
  SLA_SECTION_CARD_CLASS,
  SLA_TABLE_SCROLL_X_OEM,
  SLA_TABLE_SCROLL_Y,
} from '~/app/landing/sla/shared/sla.constants';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import { useSlaPartnerTableExpandable } from '~/app/landing/sla/shared/hooks/useSlaPartnerTableExpandable';
import type { MaintenanceSlaContractRecord } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import { toPartnershipValidity } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/utils';
import { MaintenanceSlaContractRead } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaContractRead/MaintenanceSlaContractRead';
import { useMaintenanceSlaRulesColumns } from '~/app/landing/sla/oems/components/MaintenanceSlaRulesSection/hooks/useMaintenanceSlaRulesColumns';

export interface MaintenanceSlaRulesSectionProps {
  organizationId: string;
  partners: OemPartnerGroupDto[];
  contracts: Record<string, MaintenanceSlaContractRecord>;
  loading: boolean;
  canManage: boolean;
  locale: { emptyText: ReactNode };
  onEdit: (row: OemPartnerGroupDto) => void;
}

export function MaintenanceSlaRulesSection({
  organizationId,
  partners,
  contracts,
  loading,
  canManage,
  locale,
  onEdit,
}: MaintenanceSlaRulesSectionProps) {
  const { t } = useTranslation('sla');
  const columns = useMaintenanceSlaRulesColumns({
    contracts,
    canManage,
    onEdit,
  });

  const renderExpandedRow = useCallback(
    (row: OemPartnerGroupDto) => {
      const partnership = row.slaContract
        ? toPartnershipValidity(row.slaContract)
        : null;
      const contract = contracts[row.relationshipId] ?? null;

      if (!contract || !partnership) {
        return null;
      }

      return (
        <div className="sla-table-expanded-panel">
          <MaintenanceSlaContractRead
            organizationId={organizationId}
            contract={contract}
            partnership={partnership}
          />
        </div>
      );
    },
    [contracts, organizationId],
  );

  const expandable = useSlaPartnerTableExpandable({
    renderExpandedRow,
    rowExpandable: (row) => contracts[row.relationshipId] != null,
  });

  return (
    <Card
      title={
        <Flex align="center" gap={8}>
          <IconClipboardList
            size={18}
            className="sla-section-card__title-icon"
            aria-hidden
          />
          <span className="ds-section-header">
            {t('oems.maintenance.title')}
          </span>
        </Flex>
      }
      className={SLA_SECTION_CARD_CLASS}
      styles={{ body: { padding: 0 } }}
    >
      <EvimoTable
        className="sla-partner-table"
        rowKey="relationshipId"
        loading={loading}
        columns={columns}
        dataSource={partners}
        pagination={false}
        scroll={{ x: SLA_TABLE_SCROLL_X_OEM, y: SLA_TABLE_SCROLL_Y }}
        locale={locale}
        size="middle"
        expandable={expandable}
      />
    </Card>
  );
}
